import type { RouteLocationRaw } from 'vue-router';

import { isSafeNext } from '@/platform/routing/safe-next';

// `?next=<path>` survives across multi-step login transitions (account →
// user → success) by stashing into sessionStorage at the Login mount.
// Without this the param drops the moment the user advances a step and
// the post-login redirect lands on /clusters instead of the original URL.

const KEY = 'loginNext';

export function captureLoginNext(raw: unknown): void {
  if (!isSafeNext(raw)) {
    clearLoginNext();
    return;
  }
  try {
    sessionStorage.setItem(KEY, raw);
  } catch {
    /* Safari private mode etc. — best-effort */
  }
}

export function readLoginNext(): string | null {
  try {
    const value = sessionStorage.getItem(KEY);
    return isSafeNext(value) ? value : null;
  } catch {
    return null;
  }
}

export function consumeLoginNext(): string | null {
  const value = readLoginNext();
  if (value !== null) clearLoginNext();
  return value;
}

export function clearLoginNext(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* Best-effort */
  }
}

// Threads the stashed `next` onto a router target. Used by nav helpers so
// every push from one login step to the next carries the param along —
// even when the source URL no longer has it. String form (e.g.
// `/login?step=user`) passes through unchanged — nav helpers always
// hand us object form, so the string path is just a type-safe escape
// hatch for the RouteLocationRaw union.
export function withNext<T extends RouteLocationRaw>(target: T): T {
  const next = readLoginNext();
  if (!next) return target;
  if (typeof target === 'string') return target;
  const obj = target as Exclude<RouteLocationRaw, string>;
  return { ...obj, query: { ...obj.query, next } } as unknown as T;
}
